import React, { useEffect, useState } from "react";
import API from "../services/api";
import { Table, Spinner, Alert, Button, Form, Modal } from "react-bootstrap";
import { BsWhatsapp } from "react-icons/bs";
import "./AdminSupplierDispatches.css";
import { sendPortalWhatsApp } from "../utils/whatsapp";

const fmtIST = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";

const statusPill = (status) => {
  const s = String(status || "draft").toLowerCase();
  return `sd-pill ${s}`;
};

const fileLink = (val) => {
  if (!val) return null;
  const v = String(val);
  const url = v.startsWith("data:") || /^https?:\/\//i.test(v) ? v : (v.startsWith("/") ? v : `/${v}`);
  return url;
};

const fileNameFromUrl = (url, fallback) => {
  if (!url) return fallback;
  try {
    const clean = String(url).split("?")[0];
    const name = decodeURIComponent(clean.substring(clean.lastIndexOf("/") + 1));
    return name || fallback;
  } catch (_e) {
    return fallback;
  }
};

const fallbackValue = (...values) => {
  for (const value of values) {
    const clean = String(value || "").trim();
    if (clean) return clean;
  }
  return "N/A";
};

const canReviewDispatch = (dispatch) => String(dispatch?.approvalStatus || "").toLowerCase() === "pending";
const isPaddyDispatch = (dispatch) =>
  String(dispatch?.inspectionReport?.formType || "").toLowerCase() === "paddy" ||
  /\bpaddy\b/i.test(`${dispatch?.materialName || ""} ${dispatch?.productName || ""}`);

const AdminSupplierDispatches = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("all");
  const [selectedDispatch, setSelectedDispatch] = useState(null);

  const fetchDispatches = async () => {
    setLoading(true);
    setError("");
    try {
      const params = status === "all" ? {} : { status };
      const res = await API.get("/admin/supplier-dispatches", { params });
      setRows(res.data || []);
    } catch (err) {
      setError("Failed to load supplier dispatches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDispatches();
  }, [status]);

  const handleApprove = async (id) => {
    try {
      await API.patch(`/admin/supplier-dispatches/${id}/approve`);
      setSelectedDispatch(null);
      fetchDispatches();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to approve dispatch");
    }
  };

  const handleReject = async (id) => {
    try {
      await API.patch(`/admin/supplier-dispatches/${id}/reject`, { reason: "Rejected by admin" });
      setSelectedDispatch(null);
      fetchDispatches();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to reject dispatch");
    }
  };

  const downloadAllFiles = (dispatch) => {
    if (!dispatch) return;
    const urls = [
      fileLink(dispatch.documents?.invoice),
      fileLink(dispatch.documents?.eWayBill),
      fileLink(dispatch.documents?.deliveryChallan),
      fileLink(dispatch.documents?.weighmentSlip),
    ].filter(Boolean);
    urls.forEach((u) => window.open(u, "_blank", "noopener,noreferrer"));
  };

  const sendDispatchWhatsApp = async (dispatch) => {
    const phone = dispatch?.contactMobile || dispatch?.supplierUser?.mobile || "";
    if (!phone) {
      setError("Supplier mobile/WhatsApp number not available");
      return;
    }
    try {
      const supplierName = dispatch?.companyName || dispatch?.supplierName || "Supplier";
      const trackingNumber = fallbackValue(
        dispatch?.trackingId,
        dispatch?.lrNo,
        dispatch?.lrNumber,
        dispatch?.batchNumber,
        dispatch?.purchaseOrderNo
      );
      const transporterName = fallbackValue(dispatch?.vehicle?.transporterName, dispatch?.transporterName);
      await sendPortalWhatsApp({
        to: phone,
        recipientName: supplierName,
        message: `Hello ${dispatch?.companyName || dispatch?.supplierName || "Supplier"}, dispatch for ${dispatch?.materialName || "material"} (${dispatch?.quantity || "-"} ${dispatch?.unit || ""}) is ${dispatch?.approvalStatus || "draft"}.`,
        templateVariables: [supplierName, trackingNumber, transporterName],
        sourceModule: "supplier_dispatch",
        eventType: "manual_dispatch_message",
        entityType: "SupplierDispatch",
        entityId: dispatch?._id,
        isAdmin: true,
      });
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to send WhatsApp");
    }
  };

  return (
    <div className="sd-page">
      <div className="sd-container">
        <div className="sd-topbar">
          <div className="sd-title-wrap">
            <div className="sd-icon">
              <span className="material-symbols-outlined">local_shipping</span>
            </div>
            <div>
              <div className="sd-title">Supplier Dispatches</div>
              <div className="sd-subtitle">Review, approve & track supplier dispatch entries</div>
            </div>
          </div>
          <div className="sd-actions">
            <Form.Select size="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="draft">Draft</option>
            </Form.Select>
            <button className="sd-ghost-btn" onClick={fetchDispatches}>
              <span className="material-symbols-outlined">refresh</span>
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center mt-4">
            <Spinner animation="border" variant="danger" />
          </div>
        ) : error ? (
          <Alert variant="danger" className="mt-3">{error}</Alert>
        ) : rows.length === 0 ? (
          <Alert variant="info" className="mt-3">No supplier dispatches available.</Alert>
        ) : (
          <>
            <div className="sd-card d-none d-md-block">
              <div className="table-responsive">
                <Table hover responsive className="sd-table mb-0">
                  <thead>
                    <tr>
                      <th>Supplier</th>
                      <th>Material</th>
                      <th>Qty</th>
                      <th>Vehicle</th>
                      <th>Driver</th>
                      <th>Status</th>
                      <th>Docs</th>
                      <th>Submitted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((d) => (
                      <tr key={d._id}>
                        <td>
                          <div className="fw-semibold">{d.companyName || d.supplierName || "—"}</div>
                          <div className="text-muted small">{d.contactMobile || ""}</div>
                        </td>
                        <td>
                          {d.materialName || "—"}
                          {isPaddyDispatch(d) && <div className="text-muted small">Paddy quality report</div>}
                        </td>
                        <td>{d.quantity || "—"} {d.unit || ""}</td>
                        <td>{d.vehicle?.number || "—"}</td>
                        <td>{d.vehicle?.driverName || "—"}<div className="text-muted small">{d.vehicle?.driverMobile || ""}</div></td>
                        <td><span className={statusPill(d.approvalStatus)}>{d.approvalStatus || "draft"}</span></td>
                        <td>
                          <div className="sd-docs">
                            {fileLink(d.documents?.invoice) && <a href={fileLink(d.documents.invoice)} target="_blank" rel="noreferrer">Invoice</a>}
                            {fileLink(d.documents?.eWayBill) && <a href={fileLink(d.documents.eWayBill)} target="_blank" rel="noreferrer">E-Way</a>}
                            {fileLink(d.documents?.deliveryChallan) && <a href={fileLink(d.documents.deliveryChallan)} target="_blank" rel="noreferrer">Challan</a>}
                            {fileLink(d.documents?.weighmentSlip) && <a href={fileLink(d.documents.weighmentSlip)} target="_blank" rel="noreferrer">Weighment</a>}
                          </div>
                        </td>
                        <td>{fmtIST(d.submittedAt || d.createdAt)}</td>
                        <td>
                          <div className="sd-action-stack">
                            <Button
                              size="sm"
                              className="sd-action-btn sd-action-view"
                              onClick={() => setSelectedDispatch(d)}
                            >
                              <span className="material-symbols-outlined">visibility</span>
                              View
                            </Button>
                            <Button size="sm" className="sd-action-btn sd-action-whatsapp" onClick={() => sendDispatchWhatsApp(d)}>
                              <BsWhatsapp />
                              WhatsApp
                            </Button>
                            <Button
                              size="sm"
                              className="sd-action-btn sd-action-approve"
                              disabled={!canReviewDispatch(d)}
                              title={canReviewDispatch(d) ? "Approve dispatch" : "Only pending dispatch can be approved"}
                              onClick={() => handleApprove(d._id)}
                            >
                              <span className="material-symbols-outlined">check_circle</span>
                              Approve
                            </Button>
                            {canReviewDispatch(d) && (
                              <Button size="sm" className="sd-action-btn sd-action-reject" onClick={() => handleReject(d._id)}>
                                <span className="material-symbols-outlined">cancel</span>
                                Reject
                              </Button>
                            )}
                            {!canReviewDispatch(d) && (
                              <span className="sd-action-note">
                                {String(d.approvalStatus || "draft").toLowerCase() === "approved"
                                  ? "Approved"
                                  : String(d.approvalStatus || "draft").toLowerCase() === "rejected"
                                    ? "Rejected"
                                    : "Submit pending"}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="d-block d-md-none mt-3">
              {rows.map((d) => (
                <div className="sd-mobile-card mb-3" key={d._id}>
                  <div className="fw-semibold">{d.companyName || d.supplierName || "—"}</div>
                  <div className="text-muted small">{d.contactMobile || ""}</div>
                  <div className="mt-2 small">
                    <div><strong>Material:</strong> {d.materialName || "—"}</div>
                    <div><strong>Qty:</strong> {d.quantity || "—"} {d.unit || ""}</div>
                    <div><strong>Vehicle:</strong> {d.vehicle?.number || "—"}</div>
                    <div><strong>Driver:</strong> {d.vehicle?.driverName || "—"} {d.vehicle?.driverMobile ? `(${d.vehicle.driverMobile})` : ""}</div>
                    <div><strong>Status:</strong> <span className={statusPill(d.approvalStatus)}>{d.approvalStatus || "draft"}</span></div>
                    <div><strong>Submitted:</strong> {fmtIST(d.submittedAt || d.createdAt)}</div>
                  </div>
                  <div className="sd-mobile-actions">
                    <Button
                      size="sm"
                      className="sd-action-btn sd-action-view"
                      onClick={() => setSelectedDispatch(d)}
                    >
                      <span className="material-symbols-outlined">visibility</span>
                      View
                    </Button>
                    <Button size="sm" className="sd-action-btn sd-action-whatsapp" onClick={() => sendDispatchWhatsApp(d)}>
                      <BsWhatsapp />
                      WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      className="sd-action-btn sd-action-approve"
                      disabled={!canReviewDispatch(d)}
                      title={canReviewDispatch(d) ? "Approve dispatch" : "Only pending dispatch can be approved"}
                      onClick={() => handleApprove(d._id)}
                    >
                      <span className="material-symbols-outlined">check_circle</span>
                      Approve
                    </Button>
                    {canReviewDispatch(d) && (
                      <Button size="sm" className="sd-action-btn sd-action-reject" onClick={() => handleReject(d._id)}>
                        <span className="material-symbols-outlined">cancel</span>
                        Reject
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Modal
        show={!!selectedDispatch}
        onHide={() => setSelectedDispatch(null)}
        centered
        size="xl"
        dialogClassName="sd-detail-modal"
      >
        <Modal.Header className="sdm-header">
          <div className="sdm-head-left">
            <h5 className="sdm-title mb-0">Dispatch Details</h5>
            <span className={`sdm-status ${String(selectedDispatch?.approvalStatus || "draft").toLowerCase()}`}>
              {selectedDispatch?.approvalStatus || "draft"}
            </span>
          </div>
          <div className="sdm-head-actions">
            <button className="sdm-icon-btn" onClick={() => window.print()} title="Print">
              <span className="material-symbols-outlined">print</span>
            </button>
            <button className="sdm-icon-btn" onClick={() => setSelectedDispatch(null)} title="Close">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </Modal.Header>
        <Modal.Body className="sdm-body">
          {selectedDispatch && (
            <div className="sdm-shell">
              <section className="sdm-core">
                <div className="sdm-grid">
                  <div className="sdm-col">
                    <div className="sdm-item">
                      <label>Supplier Details</label>
                      <p>{selectedDispatch.companyName || selectedDispatch.supplierName || "—"}</p>
                      <small><span className="material-symbols-outlined">phone</span>{selectedDispatch.contactMobile || "—"}</small>
                    </div>
                    <div className="sdm-item">
                      <label>Material Information</label>
                      <p>{selectedDispatch.materialName || "—"}</p>
                      <small>
                        Quantity: <b>{selectedDispatch.quantity || "—"} {selectedDispatch.unit || ""}</b>
                      </small>
                    </div>
                    <div className="sdm-item">
                      <label>Submission Timeline</label>
                      <small><span className="material-symbols-outlined">event</span>{fmtIST(selectedDispatch.submittedAt || selectedDispatch.createdAt)}</small>
                    </div>
                  </div>
                  <div className="sdm-col">
                    <div className="sdm-item">
                      <label>Vehicle Details</label>
                      <p>{selectedDispatch.vehicle?.number || "—"}</p>
                      <span className="sdm-tag">Heavy Duty Truck</span>
                    </div>
                    <div className="sdm-item">
                      <label>Driver Details</label>
                      <p>{selectedDispatch.vehicle?.driverName || "—"}</p>
                      <small><span className="material-symbols-outlined">phone</span>{selectedDispatch.vehicle?.driverMobile || "—"}</small>
                    </div>
                    <div className="sdm-item">
                      <label>Warehouse Gate</label>
                      <p>{selectedDispatch.warehouseGate || "North Receiving Dock - Gate 4"}</p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="sdm-section">
                <div className="sdm-sec-head">
                  <h6>Compliance Documents</h6>
                  <span>
                    {[
                      fileLink(selectedDispatch.documents?.invoice),
                      fileLink(selectedDispatch.documents?.eWayBill),
                      fileLink(selectedDispatch.documents?.deliveryChallan),
                      fileLink(selectedDispatch.documents?.weighmentSlip),
                    ].filter(Boolean).length}{" "}
                    Files Attached
                  </span>
                </div>
                <div className="sdm-doc-grid">
                  {[
                    {
                      key: "invoice",
                      label: "Invoice",
                      icon: "description",
                      url: fileLink(selectedDispatch.documents?.invoice),
                    },
                    {
                      key: "eWayBill",
                      label: "E-Way Bill",
                      icon: "confirmation_number",
                      url: fileLink(selectedDispatch.documents?.eWayBill),
                    },
                    {
                      key: "deliveryChallan",
                      label: "Delivery Challan",
                      icon: "local_shipping",
                      url: fileLink(selectedDispatch.documents?.deliveryChallan),
                    },
                    {
                      key: "weighmentSlip",
                      label: "Weighment Slip",
                      icon: "scale",
                      url: fileLink(selectedDispatch.documents?.weighmentSlip),
                    },
                  ].map((doc) =>
                    doc.url ? (
                      <a key={doc.key} className="sdm-doc-card" href={doc.url} target="_blank" rel="noreferrer">
                        <div className="sdm-doc-icon"><span className="material-symbols-outlined">{doc.icon}</span></div>
                        <span className="sdm-doc-name">{doc.label}</span>
                        <small>{fileNameFromUrl(doc.url, `${doc.label}.pdf`)}</small>
                      </a>
                    ) : (
                      <div key={doc.key} className="sdm-doc-card empty">
                        <div className="sdm-doc-icon"><span className="material-symbols-outlined">{doc.icon}</span></div>
                        <span className="sdm-doc-name">{doc.label}</span>
                        <small>Not Uploaded</small>
                      </div>
                    )
                  )}
                </div>
              </section>

              {isPaddyDispatch(selectedDispatch) && (
                <section className="sdm-section">
                  <div className="sdm-sec-head">
                    <h6>Paddy Quality Report</h6>
                    <span>{fmtIST(selectedDispatch.inspectionReport?.inspectionDate)}</span>
                  </div>
                  <div className="table-responsive">
                    <Table size="sm" bordered className="mb-3">
                      <thead>
                        <tr>
                          <th>Quality Parameter</th>
                          <th>Allowed %</th>
                          <th>Unloading Report</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedDispatch.inspectionReport?.qualityChecks || []).map((item, index) => (
                          <tr key={`${item.label}-${index}`}>
                            <td>{item.label || "—"}</td>
                            <td>{item.percent ?? "—"}%</td>
                            <td>{item.unloadingReport || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <div className="sdm-grid">
                    <div className="sdm-item">
                      <label>Vehicle No.</label>
                      <p>{selectedDispatch.inspectionReport?.vehicleNo || selectedDispatch.vehicle?.number || "—"}</p>
                    </div>
                    <div className="sdm-item">
                      <label>Weight</label>
                      <p>{selectedDispatch.inspectionReport?.weight ?? "—"}</p>
                    </div>
                    <div className="sdm-item">
                      <label>Short Weight</label>
                      <p>{selectedDispatch.inspectionReport?.shortWeight ?? "—"}</p>
                    </div>
                    <div className="sdm-item">
                      <label>Final Weight</label>
                      <p>{selectedDispatch.inspectionReport?.finalWeight ?? "—"}</p>
                    </div>
                  </div>
                </section>
              )}

              <section className="sdm-section">
                <h6>Additional Instructions & Notes</h6>
                <div className="sdm-notes">
                  {selectedDispatch.notes || "No notes available."}
                </div>
              </section>

              <footer className="sdm-footer">
                <div className="sdm-sync">
                  <span className="dot" />
                  <span>Auto-syncing data...</span>
                </div>
                <div className="sdm-footer-actions">
                  <button
                    className="sdm-cancel-btn"
                    onClick={() => sendDispatchWhatsApp(selectedDispatch)}
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      <BsWhatsapp />
                      <span>WhatsApp Supplier</span>
                    </span>
                  </button>
                  {canReviewDispatch(selectedDispatch) && (
                    <button
                      className="sdm-danger-btn"
                      onClick={() => selectedDispatch?._id && handleReject(selectedDispatch._id)}
                    >
                      Reject Dispatch
                    </button>
                  )}
                  <button
                    className="sdm-approve-btn"
                    disabled={!canReviewDispatch(selectedDispatch)}
                    title={canReviewDispatch(selectedDispatch) ? "Approve dispatch" : "Only pending dispatch can be approved"}
                    onClick={() => selectedDispatch?._id && handleApprove(selectedDispatch._id)}
                  >
                    <span className="material-symbols-outlined">verified</span>
                    Approve Dispatch
                  </button>
                  <button className="sdm-primary-btn" onClick={() => downloadAllFiles(selectedDispatch)}>
                    Download All Files
                  </button>
                </div>
              </footer>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminSupplierDispatches;
